import { ModelCollection } from '/@/modules/core/app/models/ModelCollection';
import { SpotlightGroup, SpotlightItem } from '@/modules/spotlight/models/SpotlightGroup';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { Estate } from '@/modules/estate/models/Estate';
import { Liability } from '@/modules/liability/models/Liability';
import { Asset } from '@/modules/asset/models/Asset';
import { Document } from '@/modules/document/models/Document';

export class SpotlightGroupCollection extends ModelCollection<SpotlightGroup> {
  constructor(items: SpotlightGroup[] | null = null) {
    super(items, SpotlightGroup);
  }

  static mapModel(items: SpotlightGroup[] | null = null): SpotlightGroupCollection {
    return new this(items);
  }

  get hasItems(): boolean {
    return this.total > 0;
  }

  get itemTempIds(): number[] | undefined {
    return this.items?.reduce(
      (acc: number[], group: SpotlightGroup) =>
        acc.concat(
          // @ts-ignore
          (group.items as SpotlightItem[]).map((item: SpotlightItem): number => item.tempId)
        ),
      []
    );
  }

  get total(): number {
    return (
      this.items?.reduce((sum: number, group: SpotlightGroup) => sum + group.items.length, 0) ?? 0
    );
  }

  get estateGroup(): SpotlightGroup | undefined {
    return this.getGroupByType(ModelType.ESTATE);
  }

  get documentGroup(): SpotlightGroup | undefined {
    return this.getGroupByType(ModelType.DOCUMENT);
  }

  get assetGroup(): SpotlightGroup | undefined {
    return this.getGroupByType(ModelType.ASSET);
  }

  get liabilityGroup(): SpotlightGroup | undefined {
    return this.getGroupByType(ModelType.LIABILITY);
  }

  getGroupByType(type: string): SpotlightGroup | undefined {
    return this.items?.find((group: SpotlightGroup) => group.type === type);
  }

  hasItemInGroup(item: SpotlightItem): boolean {
    return this.getItemsInGroup(item).some((i: SpotlightItem) => i.id === item.id);
  }

  addItemToGroup(item: SpotlightItem): void {
    this.getItemsInGroup(item).push(item);
  }

  shiftItemFromGroup(item: SpotlightItem): void {
    this.getItemsInGroup(item).shift();
  }

  private getItemsInGroup(item: SpotlightItem): SpotlightItem[] {
    if (item instanceof Estate) {
      return this.estateGroup?.items as Estate[];
    }
    if (item instanceof Document) {
      return this.documentGroup?.items as Document[];
    }
    if (item instanceof Asset) {
      return this.assetGroup?.items as Asset[];
    }
    if (item instanceof Liability) {
      return this.liabilityGroup?.items as Liability[];
    }
    return [];
  }

  static getEmptyCollection(): SpotlightGroupCollection {
    return SpotlightGroupCollection.mapModel([
      { type: ModelType.ESTATE, items: [] },
      { type: ModelType.DOCUMENT, items: [] },
      { type: ModelType.ASSET, items: [] },
      { type: ModelType.LIABILITY, items: [] }
    ]);
  }
}
